// @ts-strict-ignore
import { WireContourService } from '@3d-scene/common/util/wire-contour-service';
import { AxesHelper, Box3, BufferAttribute, BufferGeometry, Color, CubicBezierCurve3, Euler, ExtrudeGeometry, ExtrudeGeometryOptions, Float32BufferAttribute, Group, Material, Matrix4, Mesh, MeshBasicMaterial, MeshPhongMaterial, Object3D, Shape, TubeGeometry, Vector2, Vector3 } from 'three';
import { TextureElementVO } from '@article/model/vo/texture-element.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { BoundingContour } from '../../extrusion/bounding-contour';
import { MathUtils } from './math-utils';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { Face3, GeometryUtils } from './geometry-utils';
import { Font } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { IsThreeJsType } from './is-threejs-type';

const connExp = [/(^cn\d+)/, /(^ta\d+)/, /(^mp\d+)/, /(^ww\d+)/, /(^fw\d+)/, /(^la\d+)/, /(^ra\d+)/];

export class MeshUtils {

  /**
   * rotates given object by an euler rotation
   */
  public static rotateByEuler(object: Object3D, rotation: Euler): void {
    if (!object) {
      return;
    }

    object.applyMatrix4(new Matrix4().makeRotationFromEuler(rotation));
  }

  /**
   * Inverse the above Euler rotation
   * @param object
   * @param rotation
   */
  public static inverseRotateByEuler(object: Object3D, rotation: Euler): void {
    if (!object) {
      return;
    }
    const inverseRotation = new Euler(-rotation.x, -rotation.y, -rotation.z, 'ZYX');
    object.applyMatrix4((new Matrix4().makeRotationFromEuler(inverseRotation)));
  }

  /**
   * rotates given object in reversed order (ZYX)
   */
  public static rotateInReversedOrder(object: Object3D, rotateX: number, rotateY: number, rotateZ: number): void {
    if (!object) {
      return;
    }

    object.applyMatrix4(new Matrix4().makeRotationFromEuler(
      new Euler(MathUtils.toRadians(rotateX), MathUtils.toRadians(rotateY), MathUtils.toRadians(rotateZ), 'ZYX')));
  }

  /**
   * @param object object to rotate
   * @param rotationAngle angle in radians
   * @param rotationAxis rotation axis
   * @param pivotPoint the point around which the object will be rotated (optional)
   */
  public static rotateByAxis(object: Object3D, rotationAngle: number, rotationAxis: Vector3, pivotPoint: Vector3 = null): void {
    if (!object || !rotationAxis || !NumberUtils.isNumber(rotationAngle)) {
      return;
    }
    if (rotationAxis.x === 0 && rotationAxis.y === 0 && rotationAxis.z === 0) {
      return;
    }
    object.updateMatrixWorld(true);
    rotationAxis.normalize();

    const currentPosition: Vector3 = object.position.clone();

    const matrixAdd: Matrix4 = new Matrix4();
    const matrixRot: Matrix4 = new Matrix4();

    if (pivotPoint) {
      const offsetFromPivot: Vector3 = pivotPoint.clone().sub(currentPosition);
      currentPosition.add(offsetFromPivot);
    }

    matrixAdd.makeTranslation(currentPosition.x, currentPosition.y, currentPosition.z);
    const matrixSub = matrixAdd.clone().invert()
    matrixRot.makeRotationAxis(rotationAxis, rotationAngle);

    object.applyMatrix4(matrixSub);
    object.applyMatrix4(matrixRot);
    object.applyMatrix4(matrixAdd);
  }

  public static findGeometryMeshes(group: Group): Mesh[] {
    const meshes = new Array<Mesh>();
    if (!group || ArrayUtils.isEmpty(group.children)) {
      return meshes;
    }
    group.children
      .forEach(mesh => {
        if (IsThreeJsType.mesh(mesh) && !this.isConnection(mesh.name) && mesh.name !== 'axis') {
          meshes.push(mesh);
        }
      });
    return meshes;
  }

  public static getAllMeshesFromObject(object: Object3D, excludeConnection: boolean = false): any[] {
    let meshes = [];
    for (const mesh of object.children) {
      if (IsThreeJsType.mesh(mesh) || IsThreeJsType.lineSegments(mesh) || mesh.name == WireContourService.WIRECONTOUR_NAME) {
        if (!(excludeConnection && this.isConnection(mesh.name))) {
          meshes.push(mesh);
        }
      } else {
        const childMeshes = this.getAllMeshesFromObject(mesh, excludeConnection);
        meshes = meshes.concat(childMeshes);
      }
    }
    return meshes;
  }

  public static isConnection(meshName: string) {
    for (let i = 0; i < connExp.length; i++) {
      if (connExp[i].test(meshName)) {
        return true;
      }
    }
    return false;
  }

  public static isTextureElement(meshName: string): boolean {
    return meshName && meshName.startsWith(TextureElementVO.TEXTURE_ELEMENT_PREFIX);
  }

  public static getVertex2DByAxis(vertex: Vector3, axis: string): Vector2 {
    let v;
    switch (axis) {
      case 'x':
        v = new Vector2(vertex.z, vertex.y);
        break;
      case '-x':
        v = new Vector2(vertex.z, vertex.y);
        break;
      case 'y':
        v = new Vector2(vertex.x, vertex.z);
        break;
      case '-y':
        v = new Vector2(vertex.x, vertex.z);
        break;
      case 'z':
        v = new Vector2(vertex.x, vertex.y);
        break;
      case '-z':
        v = new Vector2(vertex.x, vertex.y);
        break;
      default:
        v = new Vector2(vertex.z, vertex.y);
        break;
    }
    return v;
  }

  public static findFaceNormal(faces, normal: Vector3) {
    const resultFaces = [];
    let distance, faceNormal;
    for (let k = 0; k < faces.length; k++) {
      faceNormal = faces[k].normal;

      distance = normal.distanceTo(faceNormal);
      if (distance < 0.08) {
        resultFaces.push(faces[k]);
      }
    }
    return resultFaces;
  }

  public static extrude(mesh: Mesh, depth: number, direction: string = 'x') {
    const vertices = GeometryUtils.getVertices(mesh.geometry);
    const faces = GeometryUtils.getFaces(mesh.geometry);
    const normal = this.getNormalByAxis(direction);
    const facesToExtrude = MeshUtils.findFaceNormal(faces, normal);
    if (facesToExtrude.length <= 0) {
      return null;
    }
    const boundingContour = new BoundingContour(vertices, facesToExtrude);
    const shape = this.drawShape(boundingContour.getPoints(), mesh.name, mesh.material, depth, direction);
    return shape;
  }

  public static getNormalByAxis(direction: string) {
    let normal;
    switch (direction) {
      case 'x':
        normal = new Vector3(-1, 0, 0);
        break;
      case '-x':
        normal = new Vector3(1, 0, 0);
        break;
      case 'y':
        normal = new Vector3(0, -1, 0);
        break;
      case '-y':
        normal = new Vector3(0, 1, 0);
        break;
      case 'z':
        normal = new Vector3(0, 0, -1);
        break;
      case '-z':
        normal = new Vector3(0, 0, 1);
        break;
      default:
        normal = new Vector3(-1, 0, 0);
        break;
    }
    return normal;
  }

  public static drawShape(vertices: Vector3[], name: string = '', material: Material | Material[] = new MeshPhongMaterial(), depth: number, direction: string = 'x') {
    const shape = new Shape();

    let v = MeshUtils.getVertex2DByAxis(vertices[0], direction);
    shape.moveTo(v.x, v.y);

    for (let i = 1; i < vertices.length; i++) {
      v = MeshUtils.getVertex2DByAxis(vertices[i], direction);
      shape.lineTo(v.x, v.y);
    }

    if (shape.curves.length <= 0) {
      return;
    }

    const extrudeOptions: ExtrudeGeometryOptions = {depth: depth, bevelEnabled: false, steps: 1};
    const shapeGeometry = new ExtrudeGeometry(shape, extrudeOptions);
    const shapeMesh = new Mesh(shapeGeometry, material);
    shapeMesh.name = name;

    let rotationMatrix, translationMatrix;
    switch (direction) {
      case 'x':
        rotationMatrix = new Matrix4().makeRotationY(Math.PI / 2);
        translationMatrix = new Matrix4().makeTranslation(vertices[0].x, 0, 0);
        break;
      case '-x':
        rotationMatrix = new Matrix4().makeRotationY(-Math.PI / 2);
        translationMatrix = new Matrix4().makeTranslation(vertices[0].x, 0, 0);
        break;
      case 'y':
        rotationMatrix = new Matrix4().makeRotationX(-Math.PI / 2);
        translationMatrix = new Matrix4().makeTranslation(0, vertices[0].y, 0);
        break;
      case '-y':
        rotationMatrix = new Matrix4().makeRotationX(Math.PI / 2);
        translationMatrix = new Matrix4().makeTranslation(0, vertices[0].y, 0);
        break;
      case 'z':
        rotationMatrix = new Matrix4().makeRotationY(0);
        translationMatrix = new Matrix4().makeTranslation(0, 0, vertices[0].z);
        break;
      case '-z':
        rotationMatrix = new Matrix4().makeRotationY(Math.PI);
        translationMatrix = new Matrix4().makeTranslation(0, 0, vertices[0].z);
        break;
      default:
        rotationMatrix = new Matrix4().makeRotationY(Math.PI / 2);
        translationMatrix = new Matrix4().makeTranslation(vertices[0].x, 0, 0);
        break;
    }

    shapeMesh.geometry.applyMatrix4(rotationMatrix);
    if (translationMatrix) {
      shapeMesh.geometry.applyMatrix4(translationMatrix);
    }
    GeometryUtils.setNeedsUpdate(shapeMesh.geometry);

    return shapeMesh;
  }


  public static mergeMeshesWithTarget(targetMesh: Mesh, meshes: Mesh[]): void {
    if (!targetMesh || ArrayUtils.isEmpty(meshes)) {
      return;
    }
    targetMesh.updateMatrix();
    meshes.forEach((mesh: Mesh) => {
      mesh.updateMatrix();
    });
    targetMesh.geometry = BufferGeometryUtils.mergeGeometries([targetMesh.geometry, ...meshes.map((mesh) => mesh.geometry)]);
    targetMesh.geometry.computeVertexNormals();
  }

  public static mergeMeshes(meshes: Mesh[]): BufferGeometry {
    const geometries: BufferGeometry[] = meshes
      .map((mesh) => mesh.geometry)
      .map((geometry) => geometry);

    return BufferGeometryUtils.mergeGeometries(geometries);
  }

  public static cloneMesh(mesh: Mesh): Mesh {
    if (!mesh) {
      return null;
    }
    const material: Material = mesh.material as Material;
    const clonedMesh: Mesh = mesh.clone() as Mesh;
    clonedMesh.geometry = mesh.geometry.clone();
    clonedMesh.material = material.clone();
    clonedMesh.material.needsUpdate = true;
    return clonedMesh;
  }

  public static createAxesWithLabels(axisLength: number, font: Font, labelSize: number = 16, xLabel: string = 'X', yLabel: string = 'Y', zLabel: string = 'Z'): AxesHelper {
    const axesHelper: AxesHelper = new AxesHelper(axisLength);
    if (font) {
      const meshTextX: Mesh = MeshUtils.createTextMesh(xLabel, 255, 0, 0, font, labelSize);
      const meshTextY: Mesh = MeshUtils.createTextMesh(yLabel, 0, 255, 0, font, labelSize);
      const meshTextZ: Mesh = MeshUtils.createTextMesh(zLabel, 0, 0, 255, font, labelSize);
      axesHelper.add(meshTextX);
      meshTextX.translateX(axisLength + 20);
      axesHelper.add(meshTextY);
      meshTextY.translateY(axisLength + 20);
      axesHelper.add(meshTextZ);
      meshTextZ.translateZ(axisLength + 20);
    }
    return axesHelper;
  }

  public static createCurvedTube(start: Object3D, end: Object3D, curvature: number, thickness = 15, tubularSegments = 32, radiusSegments = 6) {
    const distance = start.position.distanceTo(end.position);

    const startDir = new Vector3(0, 1, 0).applyEuler(start.rotation);
    const endDir = new Vector3(0, 1, 0).applyEuler(end.rotation);

    const controlLength = distance * curvature;
    const control1 = new Vector3().copy(start.position).add(startDir.clone().normalize().multiplyScalar(controlLength));
    const control2 = new Vector3().copy(end.position).add(endDir.clone().normalize().multiplyScalar(controlLength));

    const curve = new CubicBezierCurve3(start.position, control1, control2, end.position);

    return new TubeGeometry(curve, tubularSegments, thickness, radiusSegments, false);
  }

  public static createTextMesh(text: string, r: number, g: number, b: number, font: Font, size: number = 12, depth: number = 1, curveSegments: number = 10): Mesh {
    const textGeo: TextGeometry = new TextGeometry(text, {font, size, depth, curveSegments});
    const color: Color = new Color();
    color.setRGB(r, g, b);
    const textMaterial: MeshBasicMaterial = new MeshBasicMaterial();
    textMaterial.color = color;
    return new Mesh(textGeo, textMaterial);
  }

  public static applyMeshScaleToGeometry(mesh: Mesh): void {
    if (!mesh || !mesh.scale) {
      return;
    }
    if (mesh.scale.x === 1 && mesh.scale.y === 1 && mesh.scale.z === 1) {
      return;
    }
    if (mesh.geometry) {
      mesh.geometry.scale(mesh.scale.x, mesh.scale.y, mesh.scale.z);
    }
    mesh.scale.set(1, 1, 1);
  }

  public static applyBoxUV(geometry: BufferGeometry, transformMatrix: Matrix4 = new Matrix4(), bbox?: Box3): void {
    if (!geometry) {
      return;
    }
    if (!bbox) {
      geometry.computeBoundingBox();
      bbox = geometry.boundingBox;
    }

    const coords: number[] = [];
    const positionAttribute = geometry.attributes.position as BufferAttribute;
    coords.length = 2 * positionAttribute.array.length / 3;

    if (!geometry.attributes.uv) {
      geometry.setAttribute('uv', new Float32BufferAttribute(coords, 2));
    }

    if (geometry.index) {
      for (let vi = 0; vi < geometry.index.array.length; vi += 3) {
        const idx0 = geometry.index.array[vi];
        const idx1 = geometry.index.array[vi + 1];
        const idx2 = geometry.index.array[vi + 2];

        const positionAttribute = geometry.attributes.position as BufferAttribute;

        const vx0 = positionAttribute.array[3 * idx0];
        const vy0 = positionAttribute.array[3 * idx0 + 1];
        const vz0 = positionAttribute.array[3 * idx0 + 2];

        const vx1 = positionAttribute.array[3 * idx1];
        const vy1 = positionAttribute.array[3 * idx1 + 1];
        const vz1 = positionAttribute.array[3 * idx1 + 2];

        const vx2 = positionAttribute.array[3 * idx2];
        const vy2 = positionAttribute.array[3 * idx2 + 1];
        const vz2 = positionAttribute.array[3 * idx2 + 2];

        const v0 = new Vector3(vx0, vy0, vz0);
        const v1 = new Vector3(vx1, vy1, vz1);
        const v2 = new Vector3(vx2, vy2, vz2);

        const uvs = MeshUtils.makeUVs(v0, v1, v2, transformMatrix, bbox);

        coords[2 * idx0] = uvs.uv0.x;
        coords[2 * idx0 + 1] = uvs.uv0.y;

        coords[2 * idx1] = uvs.uv1.x;
        coords[2 * idx1 + 1] = uvs.uv1.y;

        coords[2 * idx2] = uvs.uv2.x;
        coords[2 * idx2 + 1] = uvs.uv2.y;
      }
    } else {

      const positionAttribute = geometry.attributes.position as BufferAttribute;

      for (let vi = 0; vi < positionAttribute.array.length; vi += 9) {
        const vx0 = positionAttribute.array[vi];
        const vy0 = positionAttribute.array[vi + 1];
        const vz0 = positionAttribute.array[vi + 2];

        const vx1 = positionAttribute.array[vi + 3];
        const vy1 = positionAttribute.array[vi + 4];
        const vz1 = positionAttribute.array[vi + 5];

        const vx2 = positionAttribute.array[vi + 6];
        const vy2 = positionAttribute.array[vi + 7];
        const vz2 = positionAttribute.array[vi + 8];

        const v0 = new Vector3(vx0, vy0, vz0);
        const v1 = new Vector3(vx1, vy1, vz1);
        const v2 = new Vector3(vx2, vy2, vz2);

        const uvs = MeshUtils.makeUVs(v0, v1, v2, transformMatrix, bbox);

        const idx0 = vi / 3;
        const idx1 = idx0 + 1;
        const idx2 = idx0 + 2;

        coords[2 * idx0] = uvs.uv0.x;
        coords[2 * idx0 + 1] = uvs.uv0.y;

        coords[2 * idx1] = uvs.uv1.x;
        coords[2 * idx1 + 1] = uvs.uv1.y;

        coords[2 * idx2] = uvs.uv2.x;
        coords[2 * idx2 + 1] = uvs.uv2.y;
      }
    }

    geometry.setAttribute('uv', new Float32BufferAttribute(coords, 2));
  }

  // maps 3 vertices of 1 face on the better side of the cube
  // side of the cube can be XY, XZ or YZ
  private static makeUVs(v0: Vector3, v1: Vector3, v2: Vector3, transformMatrix: Matrix4, bbox: Box3) {

    const bbox_size_x: number = bbox.max.x - bbox.min.x;
    const bbox_size_z: number = bbox.max.z - bbox.min.z;
    const bbox_size_y: number = bbox.max.y - bbox.min.y;

    // pre-rotate the model so that cube sides match world axis
    v0.applyMatrix4(transformMatrix);
    v1.applyMatrix4(transformMatrix);
    v2.applyMatrix4(transformMatrix);

    // get normal of the face, to know into which cube side it maps better
    const n = new Vector3();
    n.crossVectors(v1.clone().sub(v0), v1.clone().sub(v2)).normalize();

    n.x = Math.abs(n.x);
    n.y = Math.abs(n.y);
    n.z = Math.abs(n.z);

    const uv0 = new Vector2();
    const uv1 = new Vector2();
    const uv2 = new Vector2();

    if (n.y > n.x && n.y > n.z) {
      // xz mapping
      uv0.x = (v0.x - bbox.min.x) / bbox_size_x;
      uv0.y = (bbox.max.z - v0.z) / bbox_size_z;

      uv1.x = (v1.x - bbox.min.x) / bbox_size_x;
      uv1.y = (bbox.max.z - v1.z) / bbox_size_z;

      uv2.x = (v2.x - bbox.min.x) / bbox_size_x;
      uv2.y = (bbox.max.z - v2.z) / bbox_size_z;
    } else if (n.x > n.y && n.x > n.z) {
      // zy mapping
      uv0.x = (v0.z - bbox.min.z) / bbox_size_z;
      uv0.y = (v0.y - bbox.min.y) / bbox_size_y;

      uv1.x = (v1.z - bbox.min.z) / bbox_size_z;
      uv1.y = (v1.y - bbox.min.y) / bbox_size_y;

      uv2.x = (v2.z - bbox.min.z) / bbox_size_z;
      uv2.y = (v2.y - bbox.min.y) / bbox_size_y;
    } else if (n.z > n.y && n.z > n.x) {
      // xy mapping
      uv0.x = (v0.x - bbox.min.x) / bbox_size_x;
      uv0.y = (v0.y - bbox.min.y) / bbox_size_y;

      uv1.x = (v1.x - bbox.min.x) / bbox_size_x;
      uv1.y = (v1.y - bbox.min.y) / bbox_size_y;

      uv2.x = (v2.x - bbox.min.x) / bbox_size_x;
      uv2.y = (v2.y - bbox.min.y) / bbox_size_y;
    }

    return {
      uv0: uv0,
      uv1: uv1,
      uv2: uv2
    };
  }

  public static vectorListsAreTouching(list1: Vector3[], list2: Vector3[]): boolean {
    for (const v1 of list1) {
      for (const v2 of list2) {
        if (this.areVectorsSimilarEnough(v1, v2)) {
          return true;
        }
      }
    }
    return false;
  }

  public static areVectorsSimilarEnough(v1: Vector3, v2: Vector3): boolean {
    return v1.distanceTo(v2) < 0.1;
  }

  public static findFaceIndex(searchFace: Face3, faces: Face3[]): number {
    for (let faceIndex = 0; faceIndex < faces.length; faceIndex++) {
      const face: Face3 = faces[faceIndex];
      if (searchFace.a === face.a && searchFace.b === face.b && searchFace.c === face.c) {
        return faceIndex;
      }
    }
    return -1;
  }

  public static pushVerticesAwayFromCenter(geometry: BufferGeometry, offset: number) {
    const center: Vector3 = new Box3().setFromBufferAttribute(geometry.attributes.position as BufferAttribute).getCenter(new Vector3());
    const position = geometry.attributes.position as Float32BufferAttribute;

    for (let i = 0; i < position.count; i++) {
      const x = position.getX(i);
      const y = position.getY(i);
      const z = position.getZ(i);

      position.setXYZ(
        i,
        x >= center.x ? x + offset : x - offset,
        y >= center.y ? y + offset : y - offset,
        z >= center.z ? z + offset : z - offset
      );
    }

    position.needsUpdate = true;
  }

  public static meshGeometryIsFlat(geometry: BufferGeometry): boolean {
    const arr: Vector3[] = [];
    let maxX = -Infinity, minX = Infinity, maxY = -Infinity, minY = Infinity, maxZ = -Infinity, minZ = Infinity;

    const position = geometry.attributes.position as Float32BufferAttribute;
    const normal = geometry.attributes.normal as Float32BufferAttribute;

    if (!position || !normal) {
      return true; // Wenn keine Position oder Normalen vorhanden sind, wird die Geometrie als flach betrachtet.
    }

    for (let i = 0; i < normal.count; i++) {
      const nn = new Vector3(
        +normal.getX(i).toFixed(1),
        +normal.getY(i).toFixed(1),
        +normal.getZ(i).toFixed(1)
      );

      if (!arr.some(existing => existing.equals(nn))) {
        arr.push(nn);
      }
    }

    for (let i = 0; i < position.count; i++) {
      const vertex = new Vector3(position.getX(i), position.getY(i), position.getZ(i));
      maxX = Math.max(maxX, vertex.x);
      minX = Math.min(minX, vertex.x);

      maxY = Math.max(maxY, vertex.y);
      minY = Math.min(minY, vertex.y);

      maxZ = Math.max(maxZ, vertex.z);
      minZ = Math.min(minZ, vertex.z);
    }

    if (arr.length > 1 && (maxX - minX > 0 && maxY - minY > 0 && maxZ - minZ > 0)) {
      return false;
    }

    return true;
  }

  /** Bake a meshes transform directly into it's geometry and reset its transform */
  public static applyTransform(mesh: Mesh): void {
    mesh.updateMatrix();
    mesh.geometry.applyMatrix4(mesh.matrix);
    mesh.position.set(0, 0, 0);
    mesh.rotation.set(0, 0, 0);
    mesh.scale.set(1, 1, 1);
    mesh.updateMatrix();
  }
}
